import type { ListBuilderConfiguration } from '../types';

/**
 * Promo list configuration — drives the /settings/promos page.
 *
 * Field names match the camelCase keys returned by GET /api/promos
 * (PromoEntity shape). Empty `objectTable` is fine here since promos
 * resolve through the API path, not a list-builder JOIN.
 */
export const PROMO_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-promos-all',
			cacheTTL: 60000,
			name: 'All Promos',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'promo-name',
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'headline-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'headline', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Name' }],
					minWidth: 220,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'name', objectTable: 'promo' },
					],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'promo-discount-type',
					fields: [
						{
							id: 'discount-type-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'discountType', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'applies-at-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'appliesAt', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Type' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'discountType',
							objectTable: 'promo',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'promo-discount-value',
					fields: [
						{
							id: 'discount-value-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'discountValue', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Value' }],
					minWidth: 80,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'promo-coupon',
					fields: [
						{
							id: 'coupon-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'couponCode', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Coupon' }],
					minWidth: 100,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'couponCode',
							objectTable: 'promo',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'promo-effective-start',
					fields: [
						{
							id: 'start-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'effectiveStartDate',
									objectTable: 'promo',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
							displayConfig: { dateFormat: 'MMM d, yyyy' },
						},
					],
					headers: [{ id: 'h1', text: 'Starts' }],
					minWidth: 110,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'effectiveStartDate',
							objectTable: 'promo',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'promo-effective-end',
					fields: [
						{
							id: 'end-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'effectiveEndDate',
									objectTable: 'promo',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'date',
							displayConfig: { dateFormat: 'MMM d, yyyy' },
						},
					],
					headers: [{ id: 'h1', text: 'Ends' }],
					minWidth: 110,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'effectiveEndDate',
							objectTable: 'promo',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'top',
				},
				{
					id: 'promo-active',
					fields: [
						{
							id: 'active-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'isActive', objectTable: 'promo' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'boolean',
							displayConfig: { trueText: 'Yes', falseText: 'No' },
						},
					],
					headers: [{ id: 'h1', text: 'Active' }],
					// minWidth was 80 — too tight for "Active" header which
					// truncated to "Acti…". 96px gives the header room and
					// keeps the cell value compact.
					minWidth: 96,
					sortable: true,
					alignment: 'center',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'isActive',
							objectTable: 'promo',
						},
					],
					widthConfig: { mode: 'flexible', preset: 'small' },
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					headers: [{ id: 'actions-header', text: '' }],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'view-detail-action',
									label: 'View detail',
									iconName: 'arrow-right',
									callbackName: 'viewPromoDetail',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'promos:read',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'editPromo',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'promos:edit',
								},
								{
									id: 'clone-action',
									label: 'Clone',
									iconName: 'copy',
									callbackName: 'clonePromo',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'promos:edit',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deletePromo',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'promos:edit',
								},
							],
						},
					],
					sortable: false,
					width: 80,
					align: 'center',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: ['promoKeywords'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/settings/promos/:id',
			objectLabel: 'Promo',
			objectTable: 'promo',
			// Rows on this list are promo records keyed by `id`, so any
			// configured column of the table can be filled in server-side
			// (ListFieldHydrationService).
			rowIdField: 'id',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'headline', fieldLabel: 'Headline' },
				{ fieldName: 'discountType', fieldLabel: 'Discount Type' },
				{ fieldName: 'appliesAt', fieldLabel: 'Scope' },
				{ fieldName: 'discountValue', fieldLabel: 'Discount Value' },
				{ fieldName: 'couponCode', fieldLabel: 'Coupon Code' },
				{ fieldName: 'effectiveStartDate', fieldLabel: 'Start Date' },
				{ fieldName: 'effectiveEndDate', fieldLabel: 'End Date' },
				{ fieldName: 'isActive', fieldLabel: 'Active' },
			],
		},
	],
};
